import React, { useCallback, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
} from "@mui/material";
import Colors from "../../theme/colors.json";
import {
  COMMISSION_INFO_SECTIONS,
  COMMISSION_INFO_VALIDATIONS,
  COMMISSION_STATUS_LIST,
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  REWARD_STATUS_LIST,
  USER_GROUPS,
  handleValueRoundOff,
} from "../../constants/constants";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";

const useStyles = makeStyles((theme) => ({
  commissionInfoTableEndorsement: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
  },
  commissionInfoTableHeadingEndorsement: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  validationContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
    width: "9.6vw",
  },
  validationAction: {
    pointerEvents: "none",
  },
  endorsementDeleteBtnAction: {
    display: "flex",
    justifyContent: "flex-end",
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  commissionInfoTableHeadingCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
  commissionInfoTableCell: {
    fontSize: theme.fontSizes.SMALL,
  },
  flexColumn: {
    display: "flex",
    flexDirection: "column",
  },
  commonSelect: {
    width: "100%",
    fontSize: theme.fontSizes.SMALL,
    height: "1.875rem !important",
  },
  validationCell: {
    display: "flex",
    flexDirection: "column",
    gap: "0.7rem",
  },
}));

const CommissionInfoEndorsement = ({
  commissionInfoArray,
  setCommissionInfoArray,
  index,
}) => {
  const classes = useStyles();
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));

  const premiumSections = [
    {
      label: "Net Premium / OD (other than Motor TP, Terrorism & ERF)",
      section: COMMISSION_INFO_SECTIONS.NET_PREMIUM,
    },
    {
      label: "Motor TP Premium / Terrorism / Premium for ERF in case PL Act",
      section: COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM,
    },
  ];

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState("");

  const handleYesCommissionAmount = useCallback(() => {
    setConfirmDialogOpen(false);
    setCommissionInfoArray((prevState) => {
      const newArray = [...prevState];
      newArray[index] = {
        ...prevState[index],
        commissionAmountValidation: true,
      };
      return newArray;
    });
  }, [index, setCommissionInfoArray]);

  const handleYesRewardAmount = useCallback(() => {
    setConfirmDialogOpen(false);
    setCommissionInfoArray((prevState) => {
      const newArray = [...prevState];
      newArray[index] = {
        ...prevState[index],
        rewardAmountValidation: true,
      };
      return newArray;
    });
  }, [index, setCommissionInfoArray]);

  const handleValidationButtonClicks = (value) => {
    setSelectedValidation(value);
    setConfirmDialogOpen(true);
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };

  const handleInputChange = useCallback(
    async (e, section) => {
      const { name, value } = e.target;
      // const newValue = !isNaN(value) ? parseFloat(value) : value;
      const newValue = value;

      await setCommissionInfoArray((prevData) => {
        const newArray = [...prevData];

        const sectionData = prevData[index][section];
        const netPremium = sectionData.netPremium;
        const Commission = sectionData.commission;
        const Reward = sectionData.reward;

        const commissionAmount: any =
          (parseFloat(newValue) *
            (name === "netPremium" ? Commission : netPremium)) /
          100;

        const totalNetPremium =
          (name === "netPremium" ? parseFloat(value) : parseFloat(netPremium)) +
          (section === COMMISSION_INFO_SECTIONS.NET_PREMIUM
            ? parseFloat(prevData[index]?.motorTpPremium?.netPremium)
            : parseFloat(prevData[index]?.netPremium?.netPremium));
        const totalCommissionAmount =
          parseFloat(commissionAmount) +
          (section === COMMISSION_INFO_SECTIONS.NET_PREMIUM
            ? parseFloat(prevData[index]?.motorTpPremium?.commissionAmount)
            : parseFloat(prevData[index]?.netPremium?.commissionAmount));

        const updatedData = {
          ...prevData[index],
          [section]: {
            ...sectionData,
            [name]: newValue,
            commissionAmount: parseFloat(commissionAmount),
            reward: parseFloat(Reward),
            rewardAmount: (parseFloat(Reward) * parseFloat(netPremium)) / 100,
          },
          totalPremium: {
            totalNetPremium: handleValueRoundOff(totalNetPremium),
            totalCommissionAmount: handleValueRoundOff(totalCommissionAmount),
          },
        };

        newArray[index] = { ...updatedData };
        return newArray;
      });
    },
    [index, setCommissionInfoArray]
  );

  const handleInputChangeReward = useCallback(
    async (e, section) => {
      const { name, value } = e.target;
      // const newValue = !isNaN(value) ? parseFloat(value) : value;
      const newValue = value;

      await setCommissionInfoArray((prevData) => {
        const newArray = [...prevData];

        const sectionData = prevData[index][section];
        const netPremium = sectionData.netPremium;
        const Reward = sectionData.reward;

        const rewardAmount: any =
          (parseFloat(newValue) * parseFloat(netPremium)) / 100;

        const totalRewardAmount =
          parseFloat(rewardAmount) +
          (section === COMMISSION_INFO_SECTIONS.NET_PREMIUM
            ? parseFloat(prevData[index]?.motorTpPremium?.rewardAmount)
            : parseFloat(prevData[index]?.netPremium?.rewardAmount));

        const updatedData = {
          ...prevData[index],
          [section]: {
            ...sectionData,
            [name]: newValue,
            rewardAmount: parseFloat(rewardAmount),
          },
          totalPremium: {
            ...prevData[index]?.totalPremium,
            totalRewardAmount: handleValueRoundOff(totalRewardAmount),
          },
        };

        newArray[index] = { ...updatedData };
        return newArray;
      });
    },
    [index, setCommissionInfoArray]
  );

  const handleInputChangeSelection = useCallback(
    (e, section) => {
      const { name, value } = e.target;
      if (name === "netPremium" || name === "commission") {
        handleInputChange(e, section);
      } else if (name === "reward") {
        handleInputChangeReward(e, section);
      }
    },
    [index, setCommissionInfoArray]
  );

  const handleFieldRendering = useCallback(
    (section, field) => {
      return (
        <td key={field}>
          <TextField
            type={"number"}
            name={field}
            size="small"
            disabled={
              field === "commissionAmount" ||
              field === "rewardAmount" ||
              field === "netPremium"
                ? true
                : false
            }
            value={commissionInfoArray[index][section?.section][field]}
            onChange={(e) => handleInputChangeSelection(e, section?.section)}
            InputProps={{
              ...((field === "commission" || field === "reward") && {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }),
              ...(field !== "commission" &&
                field !== "reward" && {
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }),
              className: `${classes.tableTextFieldStyles} ${
                field === "commission"
                  ? commissionInfoArray[index]?.commissionAmountValidation ===
                    true
                    ? classes.validationAction
                    : ""
                  : commissionInfoArray[index]?.rewardAmountValidation === true
                  ? classes.validationAction
                  : ""
              }`,
            }}
          />
        </td>
      );
    },
    [classes.tableTextFieldStyles, commissionInfoArray]
  );

  const handleValidationYesClick = useCallback(
    (field) => {
      switch (field) {
        case "rewardAmount":
          return handleYesRewardAmount;
        default:
          return handleYesCommissionAmount;
      }
    },
    [setSelectedValidation, setConfirmDialogOpen]
  );

  const handleStatusChange = async (e) => {
    const { name, value } = e.target;
    await setCommissionInfoArray((prevData) => {
      const newArray = [...prevData];
      const updatedData = {
        ...newArray[index],
        [name]: value, // Update the specific property
      };
      newArray[index] = { ...updatedData };
      return newArray;
    });
  };

  useEffect(() => {
    setCommissionInfoArray((prevData) => {
      const newArray = [...prevData];
      const updatedData = {
        ...prevData[index],
        totalPremium: {
          ...prevData[index]?.totalPremium,
          totalNetPremium: handleValueRoundOff(
            parseFloat(
              prevData[index][COMMISSION_INFO_SECTIONS.NET_PREMIUM].netPremium
            ) +
              parseFloat(
                prevData[index][COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM]
                  .netPremium
              )
          ),
          totalCommissionAmount: handleValueRoundOff(
            prevData[index][COMMISSION_INFO_SECTIONS.NET_PREMIUM]
              .commissionAmount +
              prevData[index][COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM]
                .commissionAmount
          ),
          totalRewardAmount: handleValueRoundOff(
            prevData[index][COMMISSION_INFO_SECTIONS.NET_PREMIUM].rewardAmount +
              prevData[index][COMMISSION_INFO_SECTIONS.MOTORTPPREMIUM]
                .rewardAmount
          ),
        },
      };
      newArray[index] = { ...updatedData };
      return newArray;
    });
  }, [
    commissionInfoArray[index].netPremium,
    commissionInfoArray[index].motorTpPremium,
  ]);

  return (
    <div>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        title={CONFIRMATION_TITLE}
        content={CONFIRMATION_CONTENT}
        handleYes={handleValidationYesClick(selectedValidation)}
        handleNo={handleNo}
      />
      <table className={classes.commissionInfoTableEndorsement}>
        <thead>
          <tr>
            <th
              colSpan={userGroup === USER_GROUPS.UNDERWRITER ? 8 : 6}
              className={classes.commissionInfoTableHeadingEndorsement}
            >
              Endorsement Commission {index + 1}
            </th>
            {/* <th className={classes.commissionInfoTableHeadingEndorsement}>
              <div className={classes.endorsementDeleteBtnAction}>
                {index > 0 ? (
                  <IconButton onClick={() => handleDelete(index)}>
                    <Tooltip title="Delete">
                      <DeleteIcon color="secondary" />
                    </Tooltip>
                  </IconButton>
                ) : null}
              </div>
            </th> */}
          </tr>
          <tr>
            <th></th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Net Premium
            </th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Commision %
            </th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Commission Amount
            </th>
            <th className={classes.commissionInfoTableHeadingCell}>Reward %</th>
            <th className={classes.commissionInfoTableHeadingCell}>
              Reward Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {premiumSections?.map((section) => (
            <tr
              key={section?.label}
              className={classes.commissionInfoTableCell}
            >
              <td>{section?.label}</td>
              {Object.keys(commissionInfoArray[index][section?.section])?.map(
                (field) => handleFieldRendering(section, field)
              )}
            </tr>
          ))}

          <tr>
            <td className={classes.commissionInfoTableCell}>TOTAL PREMIUM</td>

            <td>
              <TextField
                type="number"
                name="netPremium"
                size="small"
                disabled
                value={
                  commissionInfoArray[index]?.totalPremium?.totalNetPremium
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
            <td>
              {/* <TextField
                type="number"
                name="gst"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              /> */}
            </td>
            <td>
              <TextField
                type="number"
                name="totalCommissionAmount"
                size="small"
                disabled
                value={
                  commissionInfoArray[index]?.totalPremium
                    ?.totalCommissionAmount
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>

            <td></td>
            <td>
              <TextField
                type="number"
                name="totalRewardAmount"
                size="small"
                disabled
                value={
                  commissionInfoArray[index]?.totalPremium?.totalRewardAmount
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                  className: classes.tableTextFieldStyles,
                }}
              />
            </td>
          </tr>
          <tr>
            <td></td>
            <td></td>
            <td></td>
            <td className={classes.validationCell}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className={
                  commissionInfoArray[index]?.commissionAmountValidation ===
                  true
                    ? classes.validationAction
                    : classes.commonBtnStyles
                }
                onClick={() =>
                  handleValidationButtonClicks(
                    COMMISSION_INFO_VALIDATIONS.Commission_Amount
                  )
                }
                disabled={
                  commissionInfoArray[index]?.commissionAmountValidation ===
                  true
                    ? true
                    : false
                }
              >
                Validation
              </Button>
              {userGroup === USER_GROUPS.ADMIN ? (
                <Button
                  className={classes.commonBtnStyles}
                  onClick={() =>
                    setCommissionInfoArray((prevState) => {
                      const newArray = [...prevState];
                      newArray[index] = {
                        ...prevState[index],
                        commissionAmountValidation: false,
                      };
                      return newArray;
                    })
                  }
                  variant="outlined"
                  size="small"
                  disabled={
                    commissionInfoArray[index]?.commissionAmountValidation ===
                    true
                      ? false
                      : true
                  }
                >
                  Revalidation
                </Button>
              ) : null}
            </td>
            <td></td>
            <td className={classes.validationCell}>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                className={
                  commissionInfoArray[index]?.rewardAmountValidation === true
                    ? classes.validationAction
                    : classes.commonBtnStyles
                }
                onClick={() =>
                  handleValidationButtonClicks(
                    COMMISSION_INFO_VALIDATIONS.Reward_Amount
                  )
                }
                disabled={
                  commissionInfoArray[index]?.rewardAmountValidation === true
                    ? true
                    : false
                }
              >
                Validation
              </Button>
              {userGroup === USER_GROUPS.ADMIN ? (
                <Button
                  className={classes.commonBtnStyles}
                  onClick={() =>
                    setCommissionInfoArray((prevState) => {
                      const newArray = [...prevState];
                      newArray[index] = {
                        ...prevState[index],
                        rewardAmountValidation: false,
                      };
                      return newArray;
                    })
                  }
                  variant="outlined"
                  size="small"
                  disabled={
                    commissionInfoArray[index]?.rewardAmountValidation === true
                      ? false
                      : true
                  }
                >
                  Revalidation
                </Button>
              ) : null}
            </td>
          </tr>
          <tr>
            <td />
            <td />
            <td colSpan={2}>
              <div className={classes.flexColumn}>
                <label className={classes.commissionInfoTableHeadingCell}>
                  Commission Status
                </label>
                <Select
                  size="small"
                  className={classes.commonSelect}
                  name="commissionStatus"
                  onChange={handleStatusChange}
                  value={commissionInfoArray[index]?.commissionStatus}
                >
                  {COMMISSION_STATUS_LIST?.map((item, index) => {
                    return (
                      <MenuItem key={index} value={item?.value}>
                        {item?.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
            </td>
            <td colSpan={2}>
              <div className={classes.flexColumn}>
                <label className={classes.commissionInfoTableHeadingCell}>
                  Reward Status
                </label>
                <Select
                  size="small"
                  className={classes.commonSelect}
                  name="rewardStatus"
                  onChange={handleStatusChange}
                  value={commissionInfoArray[index]?.rewardStatus}
                >
                  {REWARD_STATUS_LIST?.map((item, index) => {
                    return (
                      <MenuItem key={index} value={item?.value}>
                        {item?.title}
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CommissionInfoEndorsement;
